import {AccountSettingsPanel} from './account-settings-panel';
import {AccountSettingsId} from './account-settings-sidenav';
import {Trans} from '@ui/i18n/trans';
import {User} from '@ui/types/user';
import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {Switch} from '@ui/forms/toggle/switch';
import {Button} from '@ui/buttons/button';
import {RefreshIcon} from '@ui/icons/material/Refresh';
import {getNativePushDeviceInfo, NativePushDeviceInfo} from '@app/push/native-push';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';

interface Props {
  user: User;
}

interface PushPreference {
  type: string;
  label: string;
  category: string;
  enabled: boolean;
}

interface PreferencesResponse {
  preferences: PushPreference[];
}

export function PushNotificationsPanel({user}: Props) {
  const [deviceInfo, setDeviceInfo] = useState<NativePushDeviceInfo | null>(null);
  const {data, isLoading} = useQuery({
    queryKey: ['push-preferences', user.id],
    queryFn: () => fetchPreferences(),
  });
  const [preferences, setPreferences] = useState<PushPreference[]>([]);
  const updatePreferences = useUpdatePreferences();
  const refreshDeviceInfo = () => {
    getNativePushDeviceInfo()
      .then(setDeviceInfo)
      .catch(() => setDeviceInfo(null));
  };

  useEffect(() => {
    if (data?.preferences) {
      setPreferences(data.preferences);
    }
  }, [data?.preferences]);

  useEffect(() => {
    refreshDeviceInfo();
    const listener = () => refreshDeviceInfo();
    window.addEventListener('sintonizy-native-push-ready', listener);
    return () => window.removeEventListener('sintonizy-native-push-ready', listener);
  }, []);

  const setEnabled = (type: string, enabled: boolean) => {
    const next = preferences.map(preference =>
      preference.type === type ? {...preference, enabled} : preference,
    );
    setPreferences(next);
    updatePreferences.mutate({preferences: next});
  };

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.PushNotifications}
      title={<Trans message="Notificações push" />}
      titleSuffix={
        <Button
          size="xs"
          variant="outline"
          startIcon={<RefreshIcon />}
          onClick={refreshDeviceInfo}
        >
          <Trans message="Atualizar" />
        </Button>
      }
    >
      <div className="max-w-720 space-y-24">
        <div className="rounded border px-16 py-14 text-sm">
          <div className="mb-6 font-semibold">
            <Trans message="Este aparelho" />
          </div>
          <div className="text-muted">
            {deviceInfo ? (
              <span>
                {deviceInfo.platform || 'android'} ·{' '}
                {deviceInfo.notification_permission || 'unknown'} ·{' '}
                {deviceInfo.subscription_id ? (
                  <Trans message="assinatura ativa" />
                ) : (
                  <Trans message="aguardando assinatura" />
                )}
              </span>
            ) : (
              <Trans message="Ponte nativa não detectada neste navegador." />
            )}
          </div>
        </div>

        {isLoading ? (
          <ProgressCircle isIndeterminate aria-label="Carregando notificações" />
        ) : (
          <div className="divide-y rounded border">
            {preferences.map(preference => (
              <div
                key={preference.type}
                className="flex items-center justify-between gap-24 px-16 py-14"
              >
                <div>
                  <div className="font-medium">{preference.label}</div>
                  <div className="text-xs uppercase text-muted">
                    {preference.category}
                  </div>
                </div>
                <Switch
                  checked={preference.enabled}
                  disabled={updatePreferences.isPending}
                  onChange={e => setEnabled(preference.type, e.target.checked)}
                  aria-label={preference.label}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </AccountSettingsPanel>
  );
}

function fetchPreferences(): Promise<PreferencesResponse> {
  return apiClient
    .get<PreferencesResponse>('me/push-preferences')
    .then(response => response.data);
}

function useUpdatePreferences() {
  return useMutation({
    mutationFn: (payload: {preferences: Pick<PushPreference, 'type' | 'enabled'>[]}) => {
      return apiClient.put('me/push-preferences', payload).then(r => r.data);
    },
    onSuccess: () => {
      toast(message('Settings updated'), {position: 'bottom-right'});
      queryClient.invalidateQueries({queryKey: ['push-preferences']});
    },
    onError: r => showHttpErrorToast(r),
  });
}
